import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, Mail, MapPin, Clock, ArrowRight, Globe } from "lucide-react";
import { CONTACT, IMAGES } from "@/lib/data";
import { useI18n } from "@/i18n";

const NAV_ITEMS = [
  { to: "/", key: "nav.home" },
  { to: "/business-waste-service", key: "nav.business" },
  { to: "/dumpster-rentals", key: "nav.dumpsters" },
  { to: "/recycling", key: "nav.recycling" },
  { to: "/savings-calculator", key: "nav.calculator" },
  { to: "/who-we-are", key: "nav.about" },
  { to: "/contact", key: "nav.contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, t, localePath } = useI18n();
  const { pathname } = useLocation();

  // Switch to the same page in the other language.
  const switchTo =
    lang === "es"
      ? (pathname === "/es" ? "/" : pathname.replace(/^\/es/, "") || "/")
      : (pathname === "/" ? "/es" : `/es${pathname}`);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -90 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="main-navbar"
    >
      {/* Utility strip */}
      <div
        className={`hidden lg:block bg-[#0A2C2C] text-white/70 overflow-hidden transition-all duration-300 ${
          scrolled ? "max-h-0 opacity-0" : "max-h-12 opacity-100"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-8 h-9 flex items-center justify-between text-[12px]">
          <div className="flex items-center gap-6">
            <a href={CONTACT.phoneHref} className="flex items-center gap-1.5 hover:text-emerald-300 transition-colors">
              <Phone size={12} className="text-emerald-400" /> {CONTACT.phone}
            </a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-1.5 hover:text-emerald-300 transition-colors">
              <Mail size={12} className="text-emerald-400" /> {CONTACT.email}
            </a>
          </div>
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5"><Clock size={12} className="text-emerald-400" /> {t("util.hours")}</span>
            <span className="flex items-center gap-1.5"><MapPin size={12} className="text-emerald-400" /> {t("util.serving")}</span>
            <Link to={switchTo} className="flex items-center gap-1.5 hover:text-emerald-300 transition-colors font-semibold" data-testid="lang-switch-top">
              <Globe size={12} className="text-emerald-400" /> {t("lang.switch")}
            </Link>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div
        className={`transition-all duration-300 ${
          scrolled
            ? "backdrop-blur-xl bg-[#FBFAF8]/92 border-b border-[#E7E5DF] shadow-[0_8px_30px_-12px_rgba(11,15,20,0.15)]"
            : "backdrop-blur-md bg-[#FBFAF8]/80 border-b border-[#E7E5DF]/60"
        }`}
      >
        <div className={`max-w-[1400px] mx-auto px-5 sm:px-8 flex items-center justify-between gap-4 transition-all duration-300 ${scrolled ? "h-[64px]" : "h-[74px]"}`}>
          <Link to={localePath("/")} className="flex items-center gap-2.5 group shrink-0" data-testid="brand-logo">
            <img decoding="async" src={IMAGES.logoBadge} alt="EWS logo" className="w-11 h-11 rounded-xl shadow-sm group-hover:rotate-[-6deg] transition-transform duration-300" />
            <span className="leading-none">
              <span className="block font-display font-extrabold text-xl tracking-tight text-[#0B0F14]">
                EWS<span className="text-emerald-600">.</span>
              </span>
              <span className="block font-mono-tech text-[8px] uppercase tracking-[0.22em] text-[#0E3B3B]/80 mt-0.5">
                {t("brand.tagline")}
              </span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-0.5 flex-nowrap">
            {NAV_ITEMS.map((l) => (
              <NavLink
                key={l.to}
                to={localePath(l.to)}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-2.5 py-2 rounded-lg font-semibold text-[13px] whitespace-nowrap transition-all ${
                    isActive
                      ? "text-emerald-800 bg-emerald-600/10"
                      : "text-slate-700 hover:text-[#0B0F14] hover:bg-[#0B0F14]/[0.05]"
                  }`
                }
                data-testid={`nav-${l.key}`}
              >
                {t(l.key)}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-2 shrink-0">
            <Link to={switchTo} className="inline-flex items-center gap-1.5 pl-2.5 pr-3 py-2 rounded-full border border-emerald-600/40 bg-emerald-600/5 text-emerald-800 text-[12px] font-bold hover:bg-emerald-600/10 hover:border-emerald-600/60 transition-all" data-testid="lang-switch-btn" title={t("lang.switch")}>
              <Globe size={14} /> {t("lang.switch")}
            </Link>
            <a href={CONTACT.phoneHref} className="flex items-center gap-1.5 pl-2 pr-3 py-2 rounded-full border border-[#E7E5DF] text-[#0B0F14] text-[13px] font-semibold whitespace-nowrap hover:border-emerald-600/50 hover:text-emerald-700 transition-all" data-testid="phone-cta-header">
              <span className="grid place-items-center w-6 h-6 rounded-full bg-emerald-600/10 text-emerald-700"><Phone size={13} /></span>
              {CONTACT.phone}
            </a>
            <Link to={localePath("/quick-quote")} className="group inline-flex items-center gap-1.5 pl-3.5 pr-3 py-2.5 rounded-full bg-[#0E3B3B] text-white font-bold text-[13px] whitespace-nowrap hover:bg-[#0A2C2C] active:scale-95 transition-all shadow-sm" data-testid="nav-quote-btn">
              {t("cta.quickQuote")}
              <span className="grid place-items-center w-5 h-5 rounded-full bg-emerald-500 text-[#0A2C2C] group-hover:translate-x-0.5 transition-transform"><ArrowRight size={13} strokeWidth={3} /></span>
            </Link>
          </div>

          <div className="xl:hidden flex items-center gap-2">
            <Link to={switchTo} className="inline-flex items-center gap-1 px-2.5 h-11 rounded-xl border border-emerald-600/40 bg-emerald-600/5 text-emerald-800 text-[12px] font-bold active:scale-95 transition-transform" data-testid="lang-switch-mobile" title={t("lang.switch")} aria-label={t("lang.switch")}>
              <Globe size={15} /> {t("lang.label")}
            </Link>
            <button onClick={() => setOpen(!open)} className="grid place-items-center w-11 h-11 rounded-xl bg-[#0E3B3B] text-white shadow-sm active:scale-95 transition-transform" data-testid="mobile-menu-toggle" aria-label="Menu">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="xl:hidden overflow-hidden bg-[#0E3B3B] border-t border-[#0A2C2C] shadow-2xl"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-5 flex flex-col gap-1.5">
              {NAV_ITEMS.map((l) => (
                <NavLink key={l.to} to={localePath(l.to)} end={l.to === "/"} onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.key}`}
                  className={({ isActive }) => `flex items-center justify-between px-4 py-3.5 rounded-xl text-[15px] font-semibold transition-colors ${isActive ? "bg-emerald-500 text-[#0A2C2C]" : "text-white hover:bg-white/10"}`}>
                  {t(l.key)}
                  <ArrowRight size={16} className="opacity-40" />
                </NavLink>
              ))}
              <div className="mt-4 pt-5 border-t border-white/15 flex flex-col gap-4">
                <Link to={switchTo} onClick={() => setOpen(false)} className="flex items-center gap-2.5 text-white font-semibold" data-testid="mobile-lang-switch">
                  <span className="grid place-items-center w-8 h-8 rounded-full bg-white/10 text-emerald-300"><Globe size={15} /></span> {t("lang.switch")}
                </Link>
                <a href={CONTACT.phoneHref} className="flex items-center gap-2.5 text-white font-semibold">
                  <span className="grid place-items-center w-8 h-8 rounded-full bg-emerald-500 text-[#0A2C2C]"><Phone size={15} /></span> {CONTACT.phone}
                </a>
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2.5 text-white/80 text-sm">
                  <span className="grid place-items-center w-8 h-8 rounded-full bg-white/10 text-emerald-300"><Mail size={15} /></span> {CONTACT.email}
                </a>
                <Link to={localePath("/quick-quote")} onClick={() => setOpen(false)} data-testid="mobile-quote-btn"
                  className="mt-1 flex items-center justify-center gap-2 px-5 py-4 rounded-full bg-emerald-500 text-[#0A2C2C] font-bold hover:bg-emerald-400 transition-colors">
                  {t("cta.getQuickQuote")} <ArrowRight size={17} strokeWidth={3} />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
